// Apolo IDE — E2-T02 · pure trace transformer for the per-turn TRACE view.
//
// Folds the flat session_events list (as returned by GET /api/learning/events)
// into a durable, after-the-fact per-turn timeline + run-level totals so
// "which turn/model was slow or token-heavy?" is answerable after a run.
//
// Endpoint row shape (learning-events.js GET):
//   { id, session_id, type, data, model_id, tokens_in, tokens_out, created_at }
//   - `type`        : event_type — turn_start | turn_end | user_message | ...
//   - `data`        : parsed event_data_json (carries duration_ms, ok, error,
//                     and a redundant model_id on turn_end)
//   - `tokens_in/out: TOP-LEVEL COLUMNS (sessionObserver binds them on turn_end)
//   - `model_id`    : TOP-LEVEL COLUMN
//   - `created_at`  : ISO timestamp; the endpoint returns rows DESC.
//
// CONTRACT: PURE + defensive. Never throws. Non-array input → empty trace.
// Malformed rows (no `type`) are skipped. Missing/unparseable numeric fields
// surface as `null` on the turn and contribute `0` to totals (never NaN).
// E2-T01 nuance: duration_ms lives in `data` (json), tokens in the columns.

/** Coerce to a finite number or null (defensive — strings/NaN/undefined → null). */
function num(v) {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Sum helper that treats null/NaN as 0. */
function add(acc, v) {
  const n = num(v);
  return acc + (n == null ? 0 : n);
}

const EMPTY_TOTALS = {
  turnCount: 0,
  errorCount: 0,
  totalTokensIn: 0,
  totalTokensOut: 0,
  totalTokens: 0,
  totalDurationMs: 0,
  userMessages: 0,
  assistantMessages: 0,
  promptAssembled: 0,
};

/**
 * @param {Array<{ id?: string, type?: string, data?: object, model_id?: string|null,
 *   tokens_in?: number|null, tokens_out?: number|null, created_at?: string }>} events
 * @returns {{ turns: Array<object>, totals: typeof EMPTY_TOTALS }}
 */
export function buildTrace(events) {
  if (!Array.isArray(events)) {
    return { turns: [], totals: { ...EMPTY_TOTALS } };
  }

  // 1) Keep only well-formed rows (must have a `type`) and normalize to
  //    CHRONOLOGICAL order — the endpoint hands us created_at DESC.
  const rows = events
    .filter((e) => e && typeof e.type === 'string')
    .slice()
    .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));

  const turns = [];
  const totals = { ...EMPTY_TOTALS };

  // Track the most recent unpaired turn_start so a turn_end can borrow its
  // started_at. Reset after each pairing.
  let pendingStart = null;
  // E5 (S3 Glass Box): track the most recent steering_spec event so it can be
  // attached to the turn that follows. Cleared after each turn_end pairing.
  let pendingSteeringSpec = null;
  // harness-library-uplift E2 — auto-skills injetadas no prompt (skill_activated,
  // LRN3) acumulam até o turn_end seguinte: viram a pill "skill auto-injetada"
  // no TracePanel (achado da auditoria: injeção era invisível pro usuário).
  let pendingSkills = [];

  for (const r of rows) {
    switch (r.type) {
      case 'turn_start':
        pendingStart = r;
        break;

      case 'skill_activated': {
        const name = r.data && typeof r.data === 'object' ? r.data.skill_name : null;
        if (typeof name === 'string' && name) pendingSkills.push(name);
        break;
      }

      case 'steering_spec':
        // Capture for attachment to the next turn_end. Defensive: data may be absent.
        pendingSteeringSpec = (r.data && typeof r.data === 'object') ? r.data : null;
        break;

      case 'turn_end': {
        const data = r.data && typeof r.data === 'object' ? r.data : {};
        const okRaw = data.ok;
        const ok = okRaw === false ? false : true; // default true when unset
        const error = ok ? null : (typeof data.error === 'string' ? data.error : 'error');
        const duration_ms = num(data.duration_ms);
        const tokens_in = num(r.tokens_in);
        const tokens_out = num(r.tokens_out);
        // model_id: prefer the column, fall back to the json copy.
        const model_id = r.model_id ?? (typeof data.model_id === 'string' ? data.model_id : null) ?? null;
        // started_at: the paired turn_start, else fall back to this turn_end.
        const started_at = pendingStart?.created_at ?? r.created_at ?? null;

        turns.push({
          id: r.id ?? null,
          model_id,
          duration_ms,
          tokens_in,
          tokens_out,
          ok,
          error,
          started_at,
          ended_at: r.created_at ?? null,
          // E5 — attach the steering_spec snapshot that preceded this turn (null when absent).
          steeringSpec: pendingSteeringSpec ?? null,
          // E2 (library-uplift) — auto-skills injetadas neste turno ([] quando nenhuma).
          skillsActivated: pendingSkills,
        });

        totals.turnCount += 1;
        if (!ok) totals.errorCount += 1;
        totals.totalTokensIn = add(totals.totalTokensIn, tokens_in);
        totals.totalTokensOut = add(totals.totalTokensOut, tokens_out);
        totals.totalDurationMs = add(totals.totalDurationMs, duration_ms);
        pendingStart = null;
        pendingSteeringSpec = null; // E5 — consumed; clear for the next turn
        pendingSkills = []; // E2 — consumidas; zera pro próximo turno
        break;
      }

      case 'user_message':
        totals.userMessages += 1;
        break;
      case 'assistant_message':
        totals.assistantMessages += 1;
        break;
      case 'prompt_assembled':
        totals.promptAssembled += 1;
        break;
      default:
        break;
    }
  }

  totals.totalTokens = totals.totalTokensIn + totals.totalTokensOut;

  return { turns, totals };
}
